import React, { createContext, useContext, useState, useEffect } from 'react';
import { Weights, Opportunity, OpportunityScore, ScoringParams } from '../types/opportunity';
import { supabase } from '@/lib/supabase';

import { calculateScores } from '@/utils/scoringUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { loadOpportunityDataFromCSV } from '@/utils/csvLoader';

interface AssessmentContextType {
  isLoading: boolean;
  hasAttemptedLoad: boolean;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  weights: Weights;
  setWeights: (weights: Weights) => void;
  opportunities: Opportunity[];
  setOpportunities: (opportunities: Opportunity[]) => void;
  scoredOpportunities: OpportunityScore[];
  setScoredOpportunities: (opportunities: OpportunityScore[]) => void;
  scoringParams: ScoringParams;
  setScoringParams: (params: ScoringParams) => void;
  recalculateScores: () => void;
  reloadSavedAssessment: () => Promise<void>;
  isShowingSavedAssessment: boolean;
  markAsUnsaved: () => void;
}

const defaultWeights: Weights = {
  SpeedUpPermitting: 2,
  IncreaseStaffProductivity: 2,
  SimplifyApplicationExperience: 2,
  EnhanceEquitableAccessToServices: 2,
  StrengthenInternalVisibilityDecisionMaking: 2,
  IncreaseProcessTransparency: 2,
  BoostFinancialSustainability: 2,
  CostAndEffort: 2,
  Risk: 2,
  Complexity: 2,
};

export const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export const AssessmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize with loading state to prevent premature rendering
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [weights, setWeights] = useState<Weights>(defaultWeights);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [scoredOpportunities, setScoredOpportunities] = useState<OpportunityScore[]>([]);
  const [scoringParams, setScoringParams] = useState<ScoringParams>({ alpha: 0.5 });
  const [isLoading, setIsLoading] = useState(true);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [isShowingSavedAssessment, setIsShowingSavedAssessment] = useState(false);
  
  // Get router hooks for navigation
  const navigate = useNavigate();
  const location = useLocation();
  
  // Function to mark assessment as having unsaved changes
  const markAsUnsaved = () => {
    setIsShowingSavedAssessment(false);
  };
  
  // Function to recalculate scores with current weights and scoring params
  const recalculateScores = () => {
    if (opportunities.length === 0) {
      console.log('recalculateScores - No opportunities available, skipping');
      return;
    }
    const newScores = calculateScores(opportunities, weights, scoringParams);
    setScoredOpportunities(newScores);
    // Mark as unsaved when user makes changes
    markAsUnsaved();
  };

  // Load opportunity data from CSV on mount
  useEffect(() => {
    const loadOpportunityData = async () => {
      try {
        console.log('Loading opportunity data from CSV...');
        const { opportunities: csvOpportunities } = await loadOpportunityDataFromCSV();
        
        if (csvOpportunities && csvOpportunities.length > 0) {
          console.log('Successfully loaded opportunities from CSV:', csvOpportunities.length);
          setOpportunities(csvOpportunities);
          
          // Calculate initial scores with default weights
          const initialScores = calculateScores(csvOpportunities, weights, scoringParams);
          setScoredOpportunities(initialScores);
        } else {
          console.log('No opportunities loaded from CSV');
        }
      } catch (error) {
        console.log('Failed to load opportunities from CSV:', error);
      } finally {
        // Always set initialized to true, regardless of success or failure
        setIsInitialized(true);
      }
    };

    loadOpportunityData();
  }, []);

  // Helper function to load saved assessment
  const loadSavedAssessment = async () => {
    console.log('loadSavedAssessment - Starting to load');
    
    // Don't proceed if we don't have opportunities loaded
    if (opportunities.length === 0) {
      console.log('loadSavedAssessment - No opportunities available, skipping');
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }
    
    setIsLoading(true);
    setHasAttemptedLoad(true);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('loadSavedAssessment - User not logged in');
      // Always precalculate scores with default weights if no user
      const initialScores = calculateScores(opportunities, defaultWeights, scoringParams);
      setScoredOpportunities(initialScores);
      setIsShowingSavedAssessment(false); // No saved assessment
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }
    
    try {
      const { data: savedAssessment, error } = await supabase
        .from('assessments')
        .select('weights')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (error) {
        console.error('loadSavedAssessment - Error fetching assessment:', error);
        throw error;
      }
      
      if (!savedAssessment) {
        console.log('loadSavedAssessment - No saved assessment found');
        // Calculate scores with default weights
        const initialScores = calculateScores(opportunities, defaultWeights, scoringParams);
        setScoredOpportunities(initialScores);
        setIsShowingSavedAssessment(false); // No saved assessment
        return;
      }
      
      // Set weights from the saved assessment
      const savedData = savedAssessment.weights as any;
      
      // Extract scoringParams if they exist in the saved data
      let finalWeights: Weights;
      let finalScoringParams = scoringParams; // Default to current
      
      if (savedData._scoringParams) {
        // New format with embedded scoringParams
        finalScoringParams = savedData._scoringParams;
        setScoringParams(finalScoringParams);
        const { _scoringParams, ...cleanWeights } = savedData;
        finalWeights = cleanWeights as Weights;
      } else {
        // Old format without scoringParams
        finalWeights = savedData as Weights;
      }
      
      setWeights(finalWeights);
      
      // Recalculate scores using the saved weights and scoringParams
      console.log('loadSavedAssessment - Recalculating scores with saved weights and params');
      const newScores = calculateScores(opportunities, finalWeights, finalScoringParams);
      setScoredOpportunities(newScores);
      
      // Mark as showing saved assessment
      setIsShowingSavedAssessment(true);
      
      // Only move to step 2 once scores are available
      if (newScores.length > 0) {
        console.log('loadSavedAssessment - Scores loaded successfully, setting step to 2');
        setCurrentStep(2);
        
        // Make sure we're on the correct URL for step 2 (always wizard)
        const currentPath = location.pathname;
        if (currentPath === ROUTES.OPPORTUNITY_ASSESSMENT.ROOT) {
          console.log('loadSavedAssessment - Redirecting from /assessment to /assessment/wizard');
          navigate(ROUTES.OPPORTUNITY_ASSESSMENT.WIZARD);
        }
      } else {
        console.warn('loadSavedAssessment - Loaded weights but no opportunities were scored');
      }
    } catch (error) {
      console.error('loadSavedAssessment - Error:', error);
      // Calculate scores with default weights on error
      const initialScores = calculateScores(opportunities, defaultWeights, scoringParams);
      setScoredOpportunities(initialScores);
      setIsShowingSavedAssessment(false); // Error state, not saved
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    // Only load saved assessment if we have opportunities loaded
    if (opportunities.length > 0) {
      loadSavedAssessment();
    }
  }, [opportunities]);
  
  // Listen for assessment:saved events to update the state
  useEffect(() => {
    const handleSaveEvent = () => {
      console.log('Assessment saved event received in context');
      setIsShowingSavedAssessment(true);
    };
    
    window.addEventListener('assessment:saved', handleSaveEvent);
    
    return () => {
      window.removeEventListener('assessment:saved', handleSaveEvent);
    };
  }, []);
  
  // Add a method to manually load saved assessments and recalculate scores
  const reloadSavedAssessment = async () => {
    await loadSavedAssessment();
  };
  
  // Sync step and URL whenever step changes
  useEffect(() => {
    if (!isInitialized) return;
    
    const currentPath = location.pathname;
    console.log(`Step changed to ${currentStep}. Current path: ${currentPath}`);
    
    if (currentStep === 2) {
      // Step 2 should always be on the wizard path
      if (currentPath === ROUTES.OPPORTUNITY_ASSESSMENT.ROOT) {
        console.log('Step is 2 but URL is /assessment - redirecting to /assessment/wizard');
        navigate(ROUTES.OPPORTUNITY_ASSESSMENT.WIZARD);
      }
    }
  }, [currentStep, isInitialized, location.pathname, navigate]);

  // Only render children once initialization is complete
  if (!isInitialized) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AssessmentContext.Provider
      value={{
        isLoading,
        hasAttemptedLoad,
        currentStep,
        setCurrentStep,
        weights,
        setWeights: (newWeights) => {
          setWeights(newWeights);
          // Mark as unsaved when weights change
          markAsUnsaved();
        },
        opportunities,
        setOpportunities: (newOpportunities) => {
          setOpportunities(newOpportunities);
          // Recalculate scores when opportunities change
          const newScores = calculateScores(newOpportunities, weights, scoringParams);
          setScoredOpportunities(newScores);
        },
        scoredOpportunities,
        setScoredOpportunities,
        scoringParams,
        setScoringParams: (params) => {
          setScoringParams(params);
          // Mark as unsaved when scoring params change
          markAsUnsaved();
        },
        recalculateScores,
        reloadSavedAssessment,
        isShowingSavedAssessment,
        markAsUnsaved,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
};